import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EntityOp, ofEntityOp } from '@ngrx/data';
import { combineLatest, filter, first, map, merge, Observable, pairwise, take } from 'rxjs';

import { REST_API_CONNECTOR_TEMPLATE } from '@blaze-dashboard/api-builder-feature';
import { ConnectorInfo, ProjectInfo } from '@blaze-shared/api-contracts-domain';
import {
  ConnectorsCollectionService,
  ConnectorsTemplatesCollectionService,
} from '@blaze-shared/connectors-data-access';
import { ConnectorConfiguration, ConnectorTemplate } from '@blaze-shared/connectors-domain';
import { DatasetsApiService, TablesCollectionService } from '@blaze-shared/datasets-data-access';
import {
  BlazeTable,
  convertBlazeTableSchemaToBlazeTable,
  DEFAULT_BLAZE_TABLE,
  DEFAULT_LINKED_BLAZE_TABLE_CONNECTOR,
  LinkedBlazeTableConnector,
} from '@blaze-shared/datasets-domain';
import { LocalStorageService, NotificationService } from '@blaze-shared/platform-data-access';
import { compareAsc, compareAscBy } from '@blaze-shared/platform-util';
import { ProjectsCollectionService } from '@blaze-shared/projects-data-access';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { BlazeTableSchema } from '@blaze-shared/schema-domain';
import { FilterInputComponent, PanelService, ScrollerComponent } from '@blaze-shared/shared-ui';
import { deepClone } from '@blaze-shared/shared-util';

import { ConnectorMenuItemComponent } from '../connector-menu-item/connector-menu-item.component';
import { DataConnectorDialogComponent } from '../data-connector-dialog/data-connector-dialog.component';
import { ConnectorDialogAction, ConnectorDialogData } from '../models/data-connector-dialog';
import { DataConnectorsMenuPages } from '../models/data-connector-menu';
import { isRestApiConnector } from '../utils';

@UntilDestroy()
@Component({
  selector: 'blaze-data-connectors-menu',
  imports: [
    CommonModule,
    ConnectorMenuItemComponent,
    FilterInputComponent,
    MatTab,
    MatTabGroup,
    ScrollerComponent,
  ],
  templateUrl: './data-connectors-menu.component.html',
  styleUrls: ['./data-connectors-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataConnectorsMenuComponent implements OnInit {
  private readonly activeRoute = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly connectorsCollService = inject(ConnectorsCollectionService);
  private readonly connectorTemplatesCollService = inject(ConnectorsTemplatesCollectionService);
  private readonly datasetsApiService = inject(DatasetsApiService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly notificationService = inject(NotificationService);
  private readonly panelService = inject(PanelService);
  private readonly projectsCollService = inject(ProjectsCollectionService);
  private readonly schemaService = inject(SchemaService);
  private readonly tablesCollService = inject(TablesCollectionService);

  itemSelected = output<ConnectorDialogData>();

  @ViewChild('tabs') tabs!: MatTabGroup;

  protected readonly projectGroupId = signal('');

  readonly DEFAULT_LINKED_BLAZE_TABLE_CONNECTOR = DEFAULT_LINKED_BLAZE_TABLE_CONNECTOR;
  readonly REST_API_CONNECTOR_TEMPLATE = REST_API_CONNECTOR_TEMPLATE;

  readonly pages = DataConnectorsMenuPages;

  readonly accountId: string;
  readonly projectId: string;

  menuItems: any[] = []; // all menu items
  connectors: ConnectorInfo[] = [];
  templates: ConnectorTemplate[] = [];
  tables: BlazeTable[] = [];
  linkedTables: LinkedBlazeTableConnector[] = [];
  filteredConfigured$!: Observable<ConnectorConfiguration[]>;
  filteredTables$!: Observable<BlazeTable[]>;
  filteredTemplates$!: Observable<ConnectorTemplate[]>;
  configured$: Observable<ConnectorConfiguration[]>;
  templates$: Observable<ConnectorTemplate[]>;
  tables$: Observable<BlazeTableSchema[]>;

  constructor() {
    this.projectId = this.activeRoute.snapshot.params['projectId'];
    this.accountId = this.localStorageService.getAccountId();

    this.configured$ = this.connectorsCollService.entities$;
    this.templates$ = this.connectorTemplatesCollService.entities$;
    this.tables$ = this.tablesCollService.entities$;

    this.initMenuItems();
    this.refreshOnTableChanges();
  }

  ngOnInit(): void {
    this.schemaService.getAllConnectorsWithApiCalls(this.projectId).pipe(first()).subscribe();
    this.connectorTemplatesCollService.getAll();
    this.tablesCollService.clearCache();
    this.tablesCollService.getWithQuery({ projectId: this.projectId });
    this.projectsCollService.getWithQuery({ accountId: this.accountId, skipProviders: true });

    merge(this.connectorsCollService.entityActions$, this.tablesCollService.entityActions$)
      .pipe(
        untilDestroyed(this),
        ofEntityOp([
          EntityOp.ADD_ONE,
          EntityOp.ADD_MANY,
          EntityOp.UPSERT_ONE,
          EntityOp.UPSERT_MANY,
          EntityOp.SAVE_ADD_ONE,
          EntityOp.SAVE_ADD_MANY,
        ]),
      )
      .subscribe(() => {
        this.tabs.selectedIndex = 0;
        this.cdr.detectChanges();
      });
  }

  protected openCreateConnectorDialog(
    dataId?: any,
    requestedConnector?: ConnectorConfiguration,
  ): void {
    const requestedDataId =
      isRestApiConnector(requestedConnector) || dataId === REST_API_CONNECTOR_TEMPLATE.id
        ? REST_API_CONNECTOR_TEMPLATE.id
        : dataId;

    if (!requestedDataId && !requestedConnector) {
      console.error(`Unable to open dialog. ID not specified: ${requestedDataId}`);
      return;
    }

    let connector: ConnectorConfiguration | undefined;
    let template: ConnectorTemplate | undefined;

    /**
     * Created Rest api connectors has no template so have to look at connector for data.
     */
    if (requestedDataId === REST_API_CONNECTOR_TEMPLATE.id) {
      template = REST_API_CONNECTOR_TEMPLATE;
    } else {
      connector = requestedConnector || this.connectors.find(({ id }) => id === requestedDataId);
      template = this.templates.find(({ id }) => id === requestedDataId);
    }

    this.openConnectorDialog({
      action: 'create',
      connector,
      template,
    });
  }

  protected openEditConnectorDialog(
    dataId: string | undefined,
    action: ConnectorDialogAction = 'editConnector',
  ): void {
    if (!dataId) {
      throw new Error(`Unable to open dialog. ID not specified: ${dataId}`);
    }

    const connector: ConnectorInfo | undefined = this.connectors.find((c) => c.id === dataId);
    if (!connector) {
      throw new Error(`Unable to open dialog. Connector with ID ${dataId} does not exist`);
    }

    let template = this.templates.find((t) => t.id === connector.templateId);

    if (!template && isRestApiConnector(connector)) {
      template = REST_API_CONNECTOR_TEMPLATE;
    }

    this.openConnectorDialog({
      action,
      connector,
      template,
    });
  }

  protected openPreviewDialog(data: ConnectorDialogData): void {
    const datasetId = data?.previewDataset?.id;
    const action: ConnectorDialogAction = 'preview';
    if (datasetId) {
      this.datasetsApiService.getDataset(datasetId).subscribe({
        next: (dataset) => {
          this.openConnectorDialog({
            ...data,
            action,
            previewDataset: dataset,
          });
        },
        error: (error) => {
          this.notificationService.showError(error);
        },
      });
    } else {
      this.openConnectorDialog({ ...data, action });
    }
  }

  protected openCreateTableDialog(): void {
    this.openConnectorDialog({
      action: 'create',
      table: this.processBlazeTableData(DEFAULT_BLAZE_TABLE),
    });
  }

  protected openEditTableDialog(tableData?: BlazeTable): void {
    if (!tableData) {
      throw new Error('Unable to open dialog. Blaze Table not specified');
    }
    this.openConnectorDialog({
      action: 'editConnector',
      table: this.processBlazeTableData(tableData),
    });
  }

  protected openPreviewTableDialog(tableData?: BlazeTable): void {
    this.openConnectorDialog({
      action: 'preview',
      table: this.processBlazeTableData(tableData || DEFAULT_BLAZE_TABLE),
    });
  }

  protected openCreateLinkedTableDialog(): void {
    this.openConnectorDialog({
      action: 'create',
      linkedTableConnector: {
        ...DEFAULT_LINKED_BLAZE_TABLE_CONNECTOR,
        projectGroupId: this.projectGroupId(),
      },
    });
  }

  protected openEditLinkedTableDialog(
    linkedTableConnector?: LinkedBlazeTableConnector,
    table?: BlazeTable,
  ): void {
    if (table) {
      this.openEditTableDialog(table);
      return;
    }

    this.openConnectorDialog({
      action: 'editConnector',
      linkedTableConnector,
    });
  }

  protected openPreviewLinkedTableDialog(linkedTableConnector?: LinkedBlazeTableConnector): void {
    this.openConnectorDialog({
      action: 'preview',
      linkedTableConnector,
    });
  }

  protected refreshTables(): void {
    this.tablesCollService.clearCache();
    this.tablesCollService.getWithQuery({ projectId: this.projectId });
  }

  protected setFiltered([configured, templates, tables]: Observable<unknown>[]): void {
    this.filteredConfigured$ = configured as Observable<ConnectorConfiguration[]>;
    this.filteredTemplates$ = templates as Observable<ConnectorTemplate[]>;
    this.filteredTables$ = tables as Observable<BlazeTable[]>;
    combineLatest([
      this.filteredConfigured$,
      this.filteredTemplates$,
      this.filteredTables$,
      this.projectsCollService.entities$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([connectors, templates, filteredTables, allProjects]) => {
        const { tables, linkedTables } = this.processBlazeTables(filteredTables, allProjects);
        connectors = connectors.filter(({ isHidden }) => !isHidden);
        this.connectors = this.sortMenuItems(connectors);
        this.templates = this.sortMenuItems(templates);
        this.menuItems = this.sortMenuItems(this.connectors, this.templates);
        this.tables = tables;
        this.linkedTables = linkedTables;
        this.cdr.detectChanges();
      });
  }

  protected trackByIdOrName(
    index: number,
    itemWithName: Partial<{ name: string; id?: string }>,
  ): string {
    if (itemWithName.id) {
      return itemWithName.id;
    }
    return itemWithName?.name || `${index}`;
  }

  /**
   * When menu first initializes, use cached data to show available connectors without delay.
   */
  private initMenuItems(): void {
    combineLatest({
      allProjects: this.projectsCollService.entities$,
      allTables: this.tablesCollService.entities$,
      connectors: this.connectorsCollService.entities$,
      templates: this.connectorTemplatesCollService.entities$,
    })
      .pipe(
        filter(({ allProjects }) => allProjects?.length > 0),
        untilDestroyed(this),
        take(1),
      )
      .subscribe(({ allProjects, allTables, connectors, templates }) => {
        const { tables, linkedTables } = this.processBlazeTables(allTables, allProjects);
        connectors = connectors.filter(({ isHidden }) => !isHidden);

        this.connectors = this.sortMenuItems(connectors);
        this.templates = this.sortMenuItems(templates);
        this.menuItems = this.sortMenuItems(connectors, templates);
        this.tables = tables;

        this.linkedTables = linkedTables;

        const project = allProjects.find((p) => p.id === this.projectId);
        this.projectGroupId.set(project?.projectGroupId || '');
      });
  }

  /**
   * When user converts a table from a linked table to a table shared within a group, update
   * datasets list in the menu.
   */
  private refreshOnTableChanges(): void {
    combineLatest({
      allProjects: this.projectsCollService.entities$,
      allTables: this.tablesCollService.entities$,
    })
      .pipe(
        pairwise(),
        filter(([prevData, currData]) => {
          const prevTables = prevData.allTables;
          const currTables = currData.allTables;
          if (!currTables?.length) {
            return true;
          }
          let wasAnyTableShared = false;
          for (const table of currTables) {
            const prevTable = prevTables.find((t) => t.id === table.id);
            wasAnyTableShared =
              wasAnyTableShared ||
              table.isSharedWithProjectGroup !== prevTable?.isSharedWithProjectGroup;
          }
          return wasAnyTableShared;
        }),
        map(([_prevData, currData]) => currData),
        filter(({ allProjects }) => allProjects?.length > 0),
        untilDestroyed(this),
      )
      .subscribe(({ allProjects, allTables }) => {
        const { tables, linkedTables } = this.processBlazeTables(allTables, allProjects);
        this.tables = tables;
        this.linkedTables = linkedTables;

        const project = allProjects.find((p) => p.id === this.projectId);
        this.projectGroupId.set(project?.projectGroupId || '');
        this.cdr.detectChanges();
      });
  }

  private openConnectorDialog(data: ConnectorDialogData): void {
    this.panelService.open(DataConnectorDialogComponent, { data }).subscribe();
  }

  private processBlazeTableData(tableData: BlazeTable): BlazeTable {
    const table = deepClone(tableData);

    if (!tableData.id) {
      let i = 1;
      while (this.tables.some((t) => t.name === table.name)) {
        table.name = `${tableData.name} ${i}`;
        i++;
      }
    }

    return table;
  }

  /**
   * Separates blaze tables that belongs to current project and links to tables from other projects.
   * @param tables
   * @param projects
   * @private
   */
  private processBlazeTables(
    tableDatasets: BlazeTableSchema[],
    projects: ProjectInfo[],
  ): { tables: BlazeTable[]; linkedTables: LinkedBlazeTableConnector[] } {
    const processedTables: BlazeTable[] = [];
    const processedLinks = new Map<string, LinkedBlazeTableConnector>();

    tableDatasets.forEach((tableDataset) => {
      const table = convertBlazeTableSchemaToBlazeTable(tableDataset);
      const project = projects.find((p) => p.id === tableDataset.projectId);

      if (
        !tableDataset.projectId ||
        tableDataset.projectId === this.projectId ||
        tableDataset.isSharedWithProjectGroup
      ) {
        processedTables.push(table);
      } else {
        const linkedTable = processedLinks.get(tableDataset.projectId);
        if (linkedTable) {
          linkedTable.tables.push(table);
        } else {
          processedLinks.set(tableDataset.projectId, {
            name: '',
            projectGroupId: project?.projectGroupId,
            projectId: tableDataset.projectId,
            projectName: project?.name || '',
            tables: [table],
          });
        }
      }
    });

    const linkedTables = Array.from(processedLinks.values()).toSorted(compareAscBy('name'));

    return {
      tables: processedTables,
      linkedTables,
    };
  }

  private sortMenuItems(menuItems1: any[], menuItems2: any[] = []): any {
    return (menuItems1 as any).concat(menuItems2 as any).toSorted((itemA: any, itemB: any) => {
      const itemASortKey = (itemA['target'] || '') + (itemA['name'] || '');
      const itemBSortKey = (itemB['target'] || '') + (itemB['name'] || '');
      return compareAsc(itemASortKey, itemBSortKey);
    });
  }
}
